/**
 * Hero enhancements (markup: sections/pg-hero.liquid).
 *
 * <pg-copy-code>: copies a discount code to the clipboard. The chip shows a check for a
 *   moment and a toast confirms; if the Clipboard API is unavailable (old browsers, insecure
 *   context) the toast tells the shopper to type the code, which stays visible anyway.
 * <pg-hotspot>: a product dot on the hero image. Click/tap toggles its card; hover opens it on
 *   pointer devices; Escape or a click elsewhere closes it and focus stays sensible.
 */
import { PgElement, define, toast } from '@pg/core';

class PgCopyCode extends PgElement {
  /** @type {number|undefined} */
  #timer;

  mount() {
    this.refs.button?.addEventListener('click', () => this.#copy(), { signal: this.signal });
  }

  async #copy() {
    const { button } = this.refs;
    const code = this.dataset.code || '';
    try {
      await navigator.clipboard.writeText(code);
      button.classList.add('is-done');
      toast(this.dataset.copied || code);
      clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => button.classList.remove('is-done'), 2000);
    } catch {
      toast(this.dataset.failed || code, { icon: 'alert' });
    }
  }
}

class PgHotspot extends PgElement {
  /** Opened by click/tap (stays open) rather than by hover (closes on leave). */
  #pinned = false;

  mount() {
    const { toggle } = this.refs;
    if (!toggle) return;
    toggle.addEventListener(
      'click',
      () => {
        this.#pinned = !(this.#pinned && this.classList.contains('is-open'));
        this.#set(this.#pinned);
      },
      { signal: this.signal },
    );

    if (matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerenter', () => this.#set(true), { signal: this.signal });
      this.addEventListener('pointerleave', () => this.#pinned || this.#set(false), { signal: this.signal });
    }

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          this.#set(false);
          toggle.focus();
        }
      },
      { signal: this.signal },
    );
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node|null} */ (event.relatedTarget))) this.#set(false);
    }, { signal: this.signal });
    document.addEventListener(
      'click',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.#set(false);
      },
      { signal: this.signal },
    );
  }

  /** @param {boolean} open */
  #set(open) {
    if (!open) this.#pinned = false;
    this.classList.toggle('is-open', open);
    this.refs.toggle.setAttribute('aria-expanded', String(open));
    // Only one card open at a time per hero.
    if (open) {
      this.closest('.pg-hero')
        ?.querySelectorAll('pg-hotspot.is-open')
        .forEach((other) => other !== this && /** @type {PgHotspot} */ (other).close());
    }
  }

  close() {
    this.#set(false);
  }
}

define('pg-copy-code', PgCopyCode);
define('pg-hotspot', PgHotspot);
